/**
 * Filter panel + toolbar for Change History: filters, summary stats, group by, export.
 * Controls data passed to Timeline. Does NOT modify Timeline.tsx.
 */
import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useTranslation } from 'react-i18next';
import type { ChangeHistoryRow } from './ChangeHistoryTimelineModal';

function stripHtml(html: string): string {
  if (!html || typeof html !== 'string') return '';
  return String(html).replace(/<[^>]*>/g, '').trim();
}

function escapeCsvCell(value: string): string {
  const s = String(value ?? '').replace(/"/g, '""');
  if (/[,\r\n"]/.test(s)) return `"${s}"`;
  return s;
}

export function buildChangeHistoryCsv(rows: ChangeHistoryRow[]): string {
  const headers = ['Field Changed', 'Old Value', 'New Value', 'Changed By', 'Date', 'Source System'];
  const lines = [headers.map(escapeCsvCell).join(',')];
  for (const r of rows) {
    lines.push([
      escapeCsvCell(String(r.fieldChanged ?? '')),
      escapeCsvCell(stripHtml(String(r.oldValue ?? ''))),
      escapeCsvCell(stripHtml(String(r.newValue ?? ''))),
      escapeCsvCell(String(r.userName ?? '')),
      escapeCsvCell(String(r.created ?? '')),
      escapeCsvCell(String(r.externalSystem ?? '')),
    ].join(','));
  }
  return lines.join('\r\n');
}

export function downloadChangeHistoryCsv(rows: ChangeHistoryRow[], filename?: string): void {
  const csv = buildChangeHistoryCsv(rows);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename || `change-history-${new Date().toISOString().slice(0, 10)}.csv`;
  link.click();
  URL.revokeObjectURL(url);
}

export type GroupByOption = 'none' | 'fieldChanged' | 'date' | 'source';

/** Sort data by group key - returns flat array for Timeline. No section headers. */
export function sortDataByGroup(
  rows: ChangeHistoryRow[],
  groupBy: GroupByOption
): ChangeHistoryRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  if (groupBy === 'none') return [...rows];
  const getKey = (r: ChangeHistoryRow) => {
    if (groupBy === 'fieldChanged') return String(r.fieldChanged ?? '—').trim() || '—';
    if (groupBy === 'date') return r.created ? new Date(String(r.created)).toISOString().slice(0, 10) : '';
    return String(r.externalSystem ?? r.userName ?? '—').trim() || '—';
  };
  return [...rows].sort((a, b) => {
    const ka = getKey(a);
    const kb = getKey(b);
    if (groupBy === 'date') return kb.localeCompare(ka);
    return ka.localeCompare(kb);
  });
}

export function computeChangeHistorySummary(rows: ChangeHistoryRow[]) {
  const total = rows.length;
  const byFieldMap = new Map<string, number>();
  const bySourceMap = new Map<string, number>();
  const now = Date.now();
  const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
  let last7Days = 0;
  for (const r of rows) {
    const fc = String(r.fieldChanged ?? '—').trim() || '—';
    byFieldMap.set(fc, (byFieldMap.get(fc) ?? 0) + 1);
    const src = String(r.externalSystem ?? r.userName ?? '—').trim() || '—';
    bySourceMap.set(src, (bySourceMap.get(src) ?? 0) + 1);
    const t = r.created ? new Date(String(r.created)).getTime() : 0;
    if (now - t <= sevenDaysMs) last7Days++;
  }
  const byField = Array.from(byFieldMap.entries()).sort((a, b) => b[1] - a[1]);
  return { total, last7Days, mostChangedField: byField[0]?.field ?? '—' };
}

export interface ChangeHistoryFilters {
  fieldChanged: string[];
  dateFrom: string;
  dateTo: string;
  userName: string[];
  externalSystem: string[];
}

export const DEFAULT_FILTERS: ChangeHistoryFilters = {
  fieldChanged: [],
  dateFrom: '',
  dateTo: '',
  userName: [],
  externalSystem: [],
};

export function applyChangeHistoryFilters(
  rows: ChangeHistoryRow[],
  filters: ChangeHistoryFilters
): ChangeHistoryRow[] {
  if (!Array.isArray(rows) || rows.length === 0) return [];
  return rows.filter(r => {
    if (filters.fieldChanged.length > 0) {
      const fc = String(r.fieldChanged ?? '').trim();
      if (!fc || !filters.fieldChanged.includes(fc)) return false;
    }
    if (filters.userName.length > 0) {
      const un = String(r.userName ?? '').trim();
      if (!un || !filters.userName.includes(un)) return false;
    }
    if (filters.externalSystem.length > 0) {
      const es = String(r.externalSystem ?? '').trim();
      if (!es || !filters.externalSystem.includes(es)) return false;
    }
    const created = r.created ? new Date(String(r.created)).getTime() : 0;
    if (filters.dateFrom) {
      const from = new Date(filters.dateFrom).setHours(0, 0, 0, 0);
      if (created < from) return false;
    }
    if (filters.dateTo) {
      const to = new Date(filters.dateTo).setHours(23, 59, 59, 999);
      if (created > to) return false;
    }
    return true;
  });
}

function extractFilterOptions(rows: ChangeHistoryRow[]) {
  const fieldSet = new Set<string>();
  const userSet = new Set<string>();
  const systemSet = new Set<string>();
  for (const r of rows) {
    const fc = String(r.fieldChanged ?? '').trim();
    if (fc) fieldSet.add(fc);
    const un = String(r.userName ?? '').trim();
    if (un) userSet.add(un);
    const es = String(r.externalSystem ?? '').trim();
    if (es) systemSet.add(es);
  }
  return {
    fieldChanged: Array.from(fieldSet).sort(),
    userName: Array.from(userSet).sort(),
    externalSystem: Array.from(systemSet).sort(),
  };
}

export interface ChangeHistoryFilterPanelProps {
  allRows: ChangeHistoryRow[];
  displayedRows: ChangeHistoryRow[];
  filters: ChangeHistoryFilters;
  onFiltersChange: (f: ChangeHistoryFilters) => void;
  groupBy: GroupByOption;
  onGroupByChange: (g: GroupByOption) => void;
}

const GROUP_BY_OPTS: { value: GroupByOption; labelKey: string }[] = [
  { value: 'none', labelKey: 'changeHistoryToolbar.groupByNone' },
  { value: 'fieldChanged', labelKey: 'changeHistoryToolbar.groupByField' },
  { value: 'date', labelKey: 'changeHistoryToolbar.groupByDate' },
  { value: 'source', labelKey: 'changeHistoryToolbar.groupBySource' },
];

export const ChangeHistoryFilterPanel: React.FC<ChangeHistoryFilterPanelProps> = ({
  allRows,
  displayedRows,
  filters,
  onFiltersChange,
  groupBy,
  onGroupByChange,
}) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const options = React.useMemo(() => extractFilterOptions(allRows), [allRows]);
  const summary = React.useMemo(() => computeChangeHistorySummary(displayedRows), [displayedRows]);
  const hasActiveFilters =
    filters.fieldChanged.length > 0 ||
    filters.userName.length > 0 ||
    filters.externalSystem.length > 0 ||
    !!filters.dateFrom ||
    !!filters.dateTo;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1.5, py: 1.5, px: 2, borderBottom: '1px solid #e5e5e5', bgcolor: '#fafafa' }}>
      <Chip size="small" label={`${t('changeHistoryToolbar.totalChanges')}: ${summary.total}`} sx={{ fontWeight: 600, bgcolor: '#E3F2FD', color: '#1565C0' }} />
      {summary.last7Days > 0 && <Chip size="small" label={`${t('changeHistoryToolbar.last7Days')}: ${summary.last7Days}`} sx={{ bgcolor: '#E0F7FA', color: '#00695C' }} />}
      {summary.mostChangedField !== '—' && <Chip size="small" label={`${t('changeHistoryToolbar.mostChanged')}: ${summary.mostChangedField}`} sx={{ bgcolor: '#FFF3E0', color: '#E65100' }} />}
      <Box sx={{ flex: 1 }} />
      <FormControl size="small" sx={{ minWidth: 140 }}>
        <InputLabel id="ch-group-by">{t('changeHistoryToolbar.groupBy')}</InputLabel>
        <Select
          labelId="ch-group-by"
          value={groupBy}
          label={t('changeHistoryToolbar.groupBy')}
          onChange={e => onGroupByChange(e.target.value as GroupByOption)}
          sx={{ '& .MuiSelect-select': { py: 0.75 } }}
        >
          {GROUP_BY_OPTS.map(o => <MenuItem key={o.value} value={o.value}>{t(o.labelKey)}</MenuItem>)}
        </Select>
      </FormControl>
      <IconButton
        size="small"
        color={hasActiveFilters ? 'primary' : 'default'}
        onClick={e => setAnchorEl(anchorEl ? null : e.currentTarget)}
        title={t('changeHistoryToolbar.filters')}
      >
        <FilterListIcon />
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 2, minWidth: 300 } }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
          <Typography variant="subtitle2" fontWeight={600}>{t('changeHistoryToolbar.filters')}</Typography>
          <Button size="small" onClick={() => { onFiltersChange(DEFAULT_FILTERS); setAnchorEl(null); }}>
            {t('changeHistoryToolbar.reset')}
          </Button>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {options.fieldChanged.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('changeHistoryToolbar.fieldChanged')}</InputLabel>
              <Select
                multiple
                value={filters.fieldChanged}
                label={t('changeHistoryToolbar.fieldChanged')}
                onChange={e => onFiltersChange({ ...filters, fieldChanged: e.target.value as string[] })}
                renderValue={sel => (sel as string[]).join(', ')}
              >
                {options.fieldChanged.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {options.userName.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('changeHistoryToolbar.changedBy')}</InputLabel>
              <Select
                multiple
                value={filters.userName}
                label={t('changeHistoryToolbar.changedBy')}
                onChange={e => onFiltersChange({ ...filters, userName: e.target.value as string[] })}
                renderValue={sel => (sel as string[]).join(', ')}
              >
                {options.userName.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {options.externalSystem.length > 0 && (
            <FormControl size="small" fullWidth>
              <InputLabel>{t('changeHistoryToolbar.sourceSystem')}</InputLabel>
              <Select
                multiple
                value={filters.externalSystem}
                label={t('changeHistoryToolbar.sourceSystem')}
                onChange={e => onFiltersChange({ ...filters, externalSystem: e.target.value as string[] })}
                renderValue={sel => (sel as string[]).join(', ')}
              >
                {options.externalSystem.map(s => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          <TextField
            size="small"
            type="date"
            label={t('changeHistoryToolbar.dateFrom')}
            value={filters.dateFrom}
            onChange={e => onFiltersChange({ ...filters, dateFrom: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            size="small"
            type="date"
            label={t('changeHistoryToolbar.dateTo')}
            value={filters.dateTo}
            onChange={e => onFiltersChange({ ...filters, dateTo: e.target.value })}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      </Popover>
      <Button
        size="small"
        variant="outlined"
        startIcon={<FileDownloadIcon />}
        onClick={() => downloadChangeHistoryCsv(displayedRows, `change-history-${new Date().toISOString().slice(0, 10)}.csv`)}
        disabled={displayedRows.length === 0}
      >
        {t('changeHistoryToolbar.export')}
      </Button>
    </Box>
  );
};
